import { FileTypeEnum } from '../../enums/FileTypeEnum';
import type FileWithToken from '../../interfaces/FileWithToken';
import { getJwtClaim } from '../../utils/jwt/jwt';

/**
 * Claim set by the API in the viewer token (ClaimsPrincipalExtensionsService.JWT_FILE_VIEW_ENCRYPTED).
 * "True": the fileviewer wraps the whole response in the client-side AES envelope (must be decrypted
 * with decryptBlob). "False": the fileviewer serves the content in clear and, for videos, with
 * HTTP Range support.
 */
export const JWT_FILE_VIEW_ENCRYPTED_CLAIM = 'fileViewEncrypted';

/**
 * Whether a video can be played by pointing `<video src>` at the fileviewer GET URL, letting the
 * browser stream it with Range requests instead of downloading and decrypting the whole file.
 *
 * All three conditions must hold:
 * - the file is a video;
 * - the token fits in a URL (`usePost` false, e.g. no Kyber private key inside);
 * - the token says `fileViewEncrypted=false`: the client-side envelope wraps the whole body and
 *   cannot be split in ranges, so the fileviewer only streams when that claim is off.
 *
 * With tokens issued by API versions that always set `fileViewEncrypted=true`, this returns false
 * and the viewer falls back to the download-and-decrypt flow: no behavior change.
 */
export function canStreamVideo(file: FileWithToken | null | undefined): boolean {
  if (!file || file.fileType !== FileTypeEnum.Video) return false;
  if (file.usePost) return false;
  if (!file.token) return false;

  const viewEncrypted = getJwtClaim(file.token, JWT_FILE_VIEW_ENCRYPTED_CLAIM);
  return viewEncrypted !== null && viewEncrypted.toLowerCase() === 'false';
}
